// UserContext.js

import React, { createContext, useState, useEffect, useRef, useCallback } from 'react';
import { fetchWithAuth } from '../utils/fetchWithAuth';

export const UserContext = createContext();

export const UserProvider = ({ children }) => {
  const [userDetails, setUserDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isProjectsDrawerOpen, setIsProjectsDrawerOpen] = useState(false);
  const [projectsDrawerAnchor, setProjectsDrawerAnchor] = useState(null);
  const [userPlans, setUserPlans] = useState([]); 
  const [currentPlanId, setCurrentPlanId] = useState(null);
  const [currentPlanDeleted, setCurrentPlanDeleted] = useState(false);
  const [isNewPlan, setIsNewPlan] = useState(true); // Initialize based on your logic
  const [stakeholdersCustomersInvolvement, setStakeholdersCustomersInvolvement] = useState(null);
  const [isChatCollapsed, setIsChatCollapsed] = useState(true); // Track chat panel collapse state

  // Chatbot Control
  const chatbotRef = useRef();

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      fetchUserDetails(token).then(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const fetchUserDetails = async (token) => {
    try {
      const response = await fetchWithAuth('/user/details', {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      const userData = await response.json();
      setUserDetails(userData);
    } catch (error) {
      console.error('Failed to fetch user details:', error);
      setUserDetails(null);
    }
  };

  // Function to fetch saved plans for the user
  const fetchSavedPlans = async (token) => {
    try {
      console.log('UserContext: fetchSavedPlans call');
      const response = await fetchWithAuth('/plans', {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      const plans = await response.json();
      setUserPlans(plans);
    } catch (error) {
      console.error('Failed to fetch saved plans:', error);
    }
  };

  const openProjectsDrawer = useCallback((anchor = null) => {
    setProjectsDrawerAnchor(anchor);
    setIsProjectsDrawerOpen(true);
  }, []);

  const closeProjectsDrawer = useCallback(() => {
    setIsProjectsDrawerOpen(false);
    setProjectsDrawerAnchor(null);
  }, []);

  // Chatbot Control Functions
  const openChatbotWithPrompt = useCallback((prompt) => {
    if (chatbotRef.current && typeof chatbotRef.current.handleSend === 'function') {
      chatbotRef.current.toggleChat(); // Open the chatbot
      chatbotRef.current.handleSend(prompt); // Send the prefilled prompt
    } else {
      console.error("chatbotRef is not available or handleSend is not a function.");
    }
  }, []);

  const value = {
    userDetails,
    setUserDetails,
    loading,
    fetchUserDetails,
    isProjectsDrawerOpen,
    projectsDrawerAnchor,
    openProjectsDrawer,
    closeProjectsDrawer,
    userPlans,
    fetchSavedPlans,
    currentPlanId,
    setCurrentPlanId,
    currentPlanDeleted,
    setCurrentPlanDeleted,
    isNewPlan,
    setIsNewPlan,
    stakeholdersCustomersInvolvement,
    setStakeholdersCustomersInvolvement,
    chatbotRef,
    openChatbotWithPrompt,
    isChatCollapsed,
    setIsChatCollapsed,
  };

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
};
